import type React from 'react';
import { useCallback } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { usePsfSharePageData } from '#src/app/pages/psf/share/pageContext';
import { LocalSignerGoal } from '#src/app/shared/components/localSignerGoal';

import { PetitionMedia } from '../PetitionMedia';
import { useCarouselScrollTracking } from '../SharingGoalFlow/components/GoalShareActions/hooks/useCarouselScrollTracking';

import { useConstituentShareData } from './hooks/useConstituentShareData';

/**
 * Constituents-in-PSF (CHANGE-100768) share-page surface for an identified local constituent enrolled
 * in `v2` or `v4`: the new local-signer goal screen, in place of the control body. Per the Figma flows,
 * both arms reach it straight from the combo page's "Share petition" sheet; `v2`'s new share screen
 * lives inside that sheet (see the combo page's `ShareSlideUp`), not here.
 *
 * The goal screen sits inside the control page shell: the `PetitionMedia` drawer sits above it (as it
 * does for control), and the page's own footer provides the single "skip" link.
 *
 * Enrollment (treat + exposure) is handled by `useTreatConstituentPsfExperiment` at the `PageBody`
 * level so control is enrolled too; this component only renders the differing screen.
 */
export const ConstituentShareFlow: React.FC<EmptyProps> = () => {
	const { shareActions, location, userName } = useConstituentShareData();
	const {
		data: {
			petition: { id: petitionId },
		},
	} = usePsfSharePageData();
	const track = useTracking();
	const trackCarouselScroll = useCarouselScrollTracking();

	// Goal slider + channel carousel reuse the control goal-screen event names so the funnel is
	// comparable across arms (CHANGE-100773); the slider mirrors `useSetGoalStep`'s payload and, like
	// control, fires once when the signer lets go of the slider rather than on every step of a drag.
	const handleGoalChangeEnd = useCallback(
		(value: number) => {
			void track('psf_share_goal_slider_change', { goal_value: value, petition_id: petitionId });
		},
		[track, petitionId],
	);

	return (
		<>
			<PetitionMedia />
			<LocalSignerGoal
				location={location}
				userName={userName}
				shareActions={shareActions}
				onCarouselScroll={trackCarouselScroll}
				onGoalChangeEnd={handleGoalChangeEnd}
			/>
		</>
	);
};
